import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLang } from "../../lib/LangContext";
import { buildWhatsAppLink } from "../../lib/translations";
import { homeCopy } from "../../lib/homeCopy";
import { navigateAndScroll } from "../../lib/scroll";
import { useNavigate } from "react-router-dom";
import { HeroVideo } from "./HeroVideo";
import { EASE } from "./Story";

export const CinematicHero = () => {
  const { lang } = useLang();
  const c = homeCopy(lang).hero;
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const anim = (delay) => (reduce ? {} : {
    initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE, delay },
  });

  return (
    <section id="home" data-testid="hero-section" data-chapter="1" className="relative h-[100svh] min-h-[620px] w-full overflow-hidden bg-[#050505]">
      <HeroVideo />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.15) 38%, rgba(5,5,5,0.55) 72%, #050505 100%)" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent" />

      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-8 flex flex-col justify-end pb-24 md:pb-28">
        <motion.p {...anim(0.1)} className="text-[12px] tracking-[0.35em] uppercase text-gold">
          Euskirchen · Bonn · Köln
        </motion.p>
        <motion.h1 {...anim(0.2)} data-testid="hero-title" className="mt-5 font-heading font-extrabold text-white uppercase tracking-tight leading-none text-5xl sm:text-6xl lg:text-7xl">
          PUREGLOSS
        </motion.h1>
        <motion.p {...anim(0.35)} data-testid="hero-tagline" className="mt-5 font-heading text-white/95 text-xl sm:text-2xl lg:text-3xl tracking-tight">
          {c.tagline}
        </motion.p>
        <motion.p {...anim(0.5)} data-testid="hero-copy" className="mt-5 max-w-xl text-[15px] md:text-[17px] text-zinc-200 leading-relaxed">
          {c.copy}
        </motion.p>
        <motion.div {...anim(0.65)} className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            type="button"
            data-testid="hero-cta-price"
            onClick={() => navigateAndScroll(navigate, "contact", { flow: "price" })}
            className="group inline-flex items-center justify-center gap-3 bg-gold text-black px-8 py-4 text-[12px] tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C378] transition-colors duration-300"
          >
            {c.ctaPrice}
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            data-testid="hero-cta-whatsapp"
            href={buildWhatsAppLink("Hi PUREGLOSS! ")}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-[12px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300 backdrop-blur-sm"
          >
            {c.ctaWhatsapp}
          </a>
        </motion.div>
      </div>

      <a
        href="#experience"
        data-testid="hero-scroll-cue"
        onClick={(e) => { e.preventDefault(); document.getElementById("experience")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth" }); }}
        className="absolute z-10 bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-gold transition-colors"
      >
        {c.scroll}
        <span className="relative block w-px h-10 bg-white/15 overflow-hidden">
          {!reduce && <span className="absolute inset-x-0 top-0 h-1/2 bg-gold animate-[scrollcue_2.2s_ease-in-out_infinite]" />}
        </span>
        <ChevronDown size={12} className="sr-only" />
      </a>
    </section>
  );
};
